import React, { memo, useMemo } from "react";
import {
  ContentType,
  ImageType,
  SingleSelectionType,
  TextareaType,
  TypographyType,
  celltype,
} from "data/CellComponent";
import type { ProgramContentType } from "data/BlockComponent";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import dayjs from "dayjs";
import TypographyCell from "components/task/content/cell/TypographyCell";
import TextareaCell from "components/task/content/cell/TextareaCell";
import type { TaskContentV2ViewerMode } from "../TaskContentV2";
import CellContainerV2 from "./CellContainerV2";
import ButtonGroupCellV2 from "./ButtonGroupCellV2";
import FeedbackCellV2 from "./FeedbackCellV2";
import ImageCellV2 from "./ImageCellV2";

function CellV2({
  type,
  contentStr,
  blockIndex,
  lineIndex,
  cellIndex,
  setData,
  readonly,
  viewerMode = "auto",
  onAttemptEdit,
}: {
  type: celltype;
  contentStr: string;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  readonly: boolean;
  viewerMode?: TaskContentV2ViewerMode;
  onAttemptEdit?: () => void;
}) {
  const content: ContentType = useMemo(
    () => JSON.parse(contentStr),
    [contentStr]
  );

  const user = useRecoilValue(userAtom);
  const isCoach =
    viewerMode === "auto"
      ? (!!user?.accessToken && user?.role !== "patient") || false
      : viewerMode === "coach";

  const translationKey = (content as TypographyType).translationKey;
  const translatedText =
    translationKey ||
    (content as TypographyType).text ||
    (content as TypographyType).value ||
    "";

  const buttonGroupOptions =
    (content as SingleSelectionType).options?.map((option) => ({
      text: option.text || option.translationKey || "",
      showFromId: option.showFromId,
      showToId: option.showToId,
      restartFromId: option.restartFromId,
      hideFromId: option.hideFromId,
    })) || [];

  const isCoachField = content.coach;

  return (
    <CellContainerV2
      readOnly={
        readonly ||
        (isCoachField && !isCoach) ||
        (type === "buttongroup" &&
          !isCoachField &&
          isCoach &&
          (!buttonGroupOptions.some((option) => option.text === "네") ||
            !buttonGroupOptions.some((option) => option.text === "아니요")))
      }
      isCoachFieldEmpty={
        isCoach &&
        !content.optional &&
        isCoachField &&
        (content.value || "").trim().length === 0
      }
      onAttemptEdit={onAttemptEdit}
    >
      {type === "typography" ? (
        <TypographyCell {...content} text={translatedText} />
      ) : type === "dateHeader" ? (
        <TypographyCell
          {...content}
          text={`${dayjs(translatedText).format("MMM D일 ddd요일")}`}
          isHeader
        />
      ) : type === "textarea" ? (
        <TextareaCell
          readOnly={(content as TextareaType).readonly}
          placeholder={(content as TextareaType).placeholder}
          initialHtmlString={(content as TextareaType).htmlString}
          initialValue={(content as TextareaType).value}
          isCoachField={(content as TextareaType).coach}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
          id={(content as TextareaType)?.id}
        />
      ) : type === "buttongroup" ? (
        <ButtonGroupCellV2
          defaultValue={(content as SingleSelectionType).value}
          selectedIndex={(content as SingleSelectionType).selectedIndex}
          blockIndex={blockIndex}
          options={buttonGroupOptions}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
        />
      ) : type === "image" ? (
        <ImageCellV2 src={(content as ImageType).imageUrl || ""} />
      ) : type === "feedback" ? (
        <FeedbackCellV2
          savedFeedback={(content as TextareaType).value}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
        />
      ) : type === "divider" ? (
        <div />
      ) : (
        <TypographyCell
          text={`지원하지 않는 셀 타입: ${type}`}
          isAlert
        />
      )}
    </CellContainerV2>
  );
}

export default memo(CellV2);
